import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, User, ArrowRight, Video } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CRMAppointments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [filter, setFilter] = useState("upcoming");
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAppointments(); }, [filter]);

  const fetchAppointments = async () => {
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const today = new Date().toLocaleDateString("sv-SE");
      const params = {};
      if (filter === "upcoming") params.date_from = today;
      if (filter === "past") params.date_to = today;
      const res = await axios.get(`${API}/appointments`, { headers, params });
      let data = res.data;
      if (filter === "past") data = data.reverse();
      setAppointments(data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      await axios.patch(`${API}/appointments/${id}/status`, { status }, { headers });
      fetchAppointments();
    } catch { /* ignore */ }
  };

  const formatDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" });

  // Group by date
  const grouped = {};
  appointments.forEach((a) => {
    if (!grouped[a.date]) grouped[a.date] = [];
    grouped[a.date].push(a);
  });

  return (
    <div data-testid="crm-appointments">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900">Termine</h1>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {[
          { key: "upcoming", label: "Kommende" },
          { key: "past", label: "Vergangene" },
          { key: "all", label: "Alle" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => { setFilter(f.key); setLoading(true); }}
            className={`px-5 py-3 rounded-xl text-sm font-medium transition-all active:scale-95 whitespace-nowrap min-h-[44px] ${
              filter === f.key ? "bg-cyan-700 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="animate-spin h-6 w-6 border-3 border-cyan-700 border-t-transparent rounded-full" /></div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200/60 p-8 text-center">
          <CalendarDays className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Keine Termine in diesem Zeitraum</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([dateStr, apts]) => (
            <div key={dateStr}>
              <h3 className="text-sm font-semibold text-slate-500 mb-3 flex items-center gap-2">
                <CalendarDays className="h-4 w-4" /> {formatDate(dateStr)}
              </h3>
              <div className="bg-white rounded-xl border border-slate-200/60 divide-y divide-slate-100 overflow-hidden">
                {apts.map((apt) => (
                  <div key={apt.id} className="p-4 md:p-5 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 cursor-pointer flex-1 min-w-0" onClick={() => navigate(`/app/customers/${apt.customer_id}`)}>
                        <div className="h-11 w-11 bg-cyan-50 rounded-xl flex items-center justify-center shrink-0">
                          <Clock className="h-5 w-5 text-cyan-700" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm md:text-base font-semibold text-slate-800 truncate">{apt.time_slot} Uhr — {apt.name}</p>
                          <p className="text-xs text-slate-500 truncate">{apt.phone} {apt.pflegegrad ? `| PG ${apt.pflegegrad}` : ""}</p>
                        </div>
                      </div>
                      <span className={`text-xs font-medium px-2.5 py-1.5 rounded-full whitespace-nowrap shrink-0 ${
                        apt.status === "confirmed" ? "bg-green-50 text-green-700" :
                        apt.status === "completed" ? "bg-slate-100 text-slate-600" :
                        apt.status === "cancelled" ? "bg-red-50 text-red-600" :
                        "bg-amber-50 text-amber-700"
                      }`}>
                        {apt.status === "confirmed" ? "Bestätigt" : apt.status === "completed" ? "Abgeschlossen" : apt.status === "cancelled" ? "Storniert" : "Offen"}
                      </span>
                    </div>
                    {/* Action buttons on own row for touch */}
                    {apt.status === "confirmed" && (
                      <div className="flex flex-wrap gap-2 mt-3 ml-14">
                        <Button size="sm" className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl text-xs h-10 px-4 active:scale-95" onClick={() => navigate(`/app/video/${apt.id}`)} data-testid={`video-btn-${apt.id}`}>
                          <Video className="h-4 w-4 mr-1.5" /> Video starten
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-xl text-xs h-10 px-4 active:scale-95" onClick={() => updateStatus(apt.id, "completed")}>Abgeschlossen</Button>
                        <Button size="sm" variant="outline" className="rounded-xl text-xs h-10 px-4 text-red-600 hover:text-red-700 hover:bg-red-50 active:scale-95" onClick={() => updateStatus(apt.id, "cancelled")}>Stornieren</Button>
                        <Button size="sm" variant="ghost" className="rounded-xl text-xs h-10 px-4 active:scale-95" onClick={() => navigate(`/app/beratung/new/${apt.id}`)}>
                          Bogen <ArrowRight className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </div>
                    )}
                    {apt.status !== "confirmed" && (
                      <div className="flex gap-2 mt-3 ml-14">
                        <Button size="sm" variant="ghost" className="rounded-xl text-xs h-10 px-4 active:scale-95" onClick={() => navigate(`/app/beratung/new/${apt.id}`)}>
                          Bogen <ArrowRight className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
